import React, { useMemo, useState } from 'react';
import { BookOpen, Briefcase, FlaskConical, Flower2, LucideIcon, TrendingUp } from 'lucide-react';
import { BooksSection } from '../../data/profileData';
import { Card, Icon, ResponsiveImage, Section, Tag } from '../ui';
import './Books.css';

interface BooksProps {
  books: BooksSection;
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  business: Briefcase,
  nonfiction: BookOpen,
  religion: Flower2,
  science: FlaskConical,
  selfdev: TrendingUp,
};

/** The reading list: category filter and a grid of book cards with a one-line take on each. */
const Books: React.FC<BooksProps> = ({ books: { books, categories } }) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const countFor = (categoryId: string) => books.filter((book) => book.category === categoryId).length;
  // Only offer categories that currently have books.
  const usedCategories = categories.filter((category) => countFor(category.id) > 0);
  const labelFor = (categoryId: string) => categories.find((c) => c.id === categoryId)?.label ?? categoryId;

  const visibleBooks = useMemo(
    () =>
      books
        .filter((book) => activeCategory === 'all' || book.category === activeCategory)
        .sort((a, b) => a.title.localeCompare(b.title)),
    [books, activeCategory]
  );

  return (
    <Section
      id="reading-list"
      title="Reading List"
      eyebrow="Books that shaped my thinking"
      intro="The books I return to on focus, discipline, craft, and leadership."
      className="reading-list"
    >
      <div className="ui-filter reading-list__filter" role="group" aria-label="Filter books by category">
        <button
          type="button"
          className="ui-filter__option"
          aria-pressed={activeCategory === 'all'}
          onClick={() => setActiveCategory('all')}
        >
          All
          <span className="ui-filter__count">{books.length}</span>
        </button>
        {usedCategories.map((category) => (
          <button
            key={category.id}
            type="button"
            className="ui-filter__option"
            aria-pressed={activeCategory === category.id}
            onClick={() => setActiveCategory(category.id)}
          >
            <Icon icon={CATEGORY_ICONS[category.id] ?? BookOpen} size={16} />
            {category.label}
            <span className="ui-filter__count">{countFor(category.id)}</span>
          </button>
        ))}
      </div>

      <p className="visually-hidden" aria-live="polite">{`${visibleBooks.length} books shown.`}</p>

      <ul className="reading-list__grid">
        {visibleBooks.map((book) => (
          <Card as="li" key={book.id} className="book">
            <div className="book__cover">
              {book.coverSrc ? (
                <ResponsiveImage path={book.coverSrc} alt={`Cover of ${book.title}`} className="book__cover-img" />
              ) : (
                <Icon icon={BookOpen} size={32} />
              )}
            </div>
            <div className="book__body">
              <h3 className="book__title">{book.title}</h3>
              <p className="book__author">{book.author}</p>
              {book.description && <p className="book__description">{book.description}</p>}
              <Tag className="book__category">{labelFor(book.category)}</Tag>
            </div>
          </Card>
        ))}
      </ul>
    </Section>
  );
};

export default Books;
